<template>
  <page-template :is-loading="isLoading" :data-exists="selectedEdgeFunctionData !== null">
    <template #pageHeadline>
      {{
        isNew
          ? $t('edgeFunction.configPage.newPageHeadline')
          : $t('edgeFunction.configPage.editPageHeadline')
      }}
    </template>
    <template #secondLevelHeaderLeft v-if="!readOnly">
      <rbz-form-button
        variant="tertiary"
        icon="IconArrowBack"
        v-b-tooltip="$t('common.operations.returnToList')"
        link-route-name="Edge Functions List"
        classes="redirect-to-list-button"
        data-qa="redirect-to-list-button"
      />
      <template v-if="selectedEdgeFunction && !isNew">
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
        <rbz-dropdown
          :items="dropdownData.map((item) => [item.id, item.name])"
          :disabled="isLoading"
          v-model:value="selectedEdgeFunction"
          @update:value="switchEdgeFunction($event)"
          :ignore-update-in-process="true"
          type="single-select"
          :header-tooltip="$t('edgeFunction.configPage.switchTooltip')"
          classes="switch-edge-function switch-page-entry"
          data-qa="switch-edge-function"
        />
      </template>
    </template>
    <template #secondLevelHeaderRight v-if="!readOnly">
      <template v-if="selectedEdgeFunction && !isNew">
        <rbz-form-button
          :label="$t('common.operations.duplicate.label')"
          variant="tertiary"
          icon="IconDuplicate"
          @button-click="duplicateEdgeFunction()"
          class="duplicate-button"
          :disabled="isLoading || entityNotSaved"
          :tooltip="
            entityNotSaved ? $t('common.operations.duplicate.disabledTooltip.notSaved') : ''
          "
          data-qa="duplicate-button"
        />
        <rbz-form-button
          :label="$t('common.operations.download.label')"
          variant="tertiary"
          icon="IconDownload"
          @button-click="downloadEdgeFunction()"
          class="download-button"
          :disabled="isLoading"
          data-qa="download-button"
        />
        <rbz-form-button
          :label="$t('common.operations.delete.label')"
          variant="tertiary"
          icon="IconDelete"
          @button-click="openDeleteEdgeFunctionModal()"
          class="delete-button"
          :disabled="isLoading || selectedEdgeFunctionProtected || selectedEdgeFunctionReferenced"
          :tooltip="deleteTooltip"
          data-qa="delete-button"
        />
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
      </template>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !isEdgeFunctionValid"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="name"
              classes="name"
              data-qa="name-input"
              :label="$t('edgeFunction.configPage.name')"
              :placeholder="$t('edgeFunction.configPage.name')"
              v-model:inputValue="selectedEdgeFunctionData.name"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="description"
              classes="description"
              data-qa="description-textarea"
              :label="$t('edgeFunction.configPage.description')"
              v-model:inputValue="selectedEdgeFunctionData.description"
              rows="2"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-dropdown
              v-if="cloudPhases.length"
              :items="getCloudPhasesDropdownItems"
              v-model:value="selectedEdgeFunctionData.phase"
              :label="$t('edgeFunction.phase')"
              :skip-sort="true"
              class="phase-selection"
              data-qa="cloud-phase-dropdown"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <label class="rbz-label"> Tags </label>
            <div class="automatic-tags">
              <template v-for="tag in automaticTags" :key="tag">
                <rbz-custom-tag :value="tag" :disabled="readOnly" />
              </template>
            </div>
          </div>
        </div>
        <div class="rbz-column is-8">
          <div class="rbz-field">
            <rbz-form-textarea
              name="code"
              classes="code"
              data-qa="code-textarea"
              :label="$t('edgeFunction.code')"
              v-model:inputValue="selectedEdgeFunctionData.code"
              rows="20"
              :disabled="readOnly"
            />
          </div>
        </div>
      </div>
      <rbz-card-collapsible
        :is-collapsed="false"
        class="security-policies-connections-card"
        data-qa="security-policies-connections-card"
      >
        <template #title>
          {{ $t('edgeFunction.configPage.securityPoliciesConnections.title') }}
        </template>
        <template #content>
          <rbz-table
            :columns="connectionsColumns"
            :table-data="connections"
            ref="connectionsTable"
            :row-buttons="connectionsRowButtons"
            rows-per-page="20"
            data-qa="connections-table"
            :show-buttons-column="!readOnly"
            :show-new-button="!readOnly"
            :show-new-as-row="true"
            :show-download-button="false"
            :show-filter-button="false"
            :show-new-default-data="getConnectionDefaultValues()"
            :new-button-label="
              $t('edgeFunction.configPage.securityPoliciesConnections.newButtonLabel')
            "
            @add-inline-data="addConnection($event)"
            @delete-row-button-clicked="removeConnection($event)"
          >
            <template #newRowOverride="newRowProps">
              <template v-if="newConnectionSecurityPolicies?.length === 1">
                <td class="all-connected-data-cell" colspan="3">
                  <div class="data-cell-content ellipsis has-text-centered">
                    <span>
                      {{ $t('edgeFunction.configPage.securityPoliciesConnections.allConnected') }}
                    </span>
                  </div>
                </td>
                <td>
                  <form-confirmation :show-confirm="false" @cancel="cancelAddConnection()" />
                </td>
              </template>
              <template v-else>
                <td class="new-data-cell">
                  <rbz-dropdown
                    class="new-security-policy-selection name-selection"
                    :items="
                      newConnectionSecurityPolicies.map((item) => {
                        return [item.id, item.name];
                      })
                    "
                    :skip-sort="true"
                    data-qa="new-connection-security-policy-dropdown"
                    v-model:value="newRowProps.data.securityPolicyId"
                    @update:value="newConnectionOnChangeSecurityPolicy(newRowProps.data)"
                  />
                </td>
                <td class="new-data-cell">
                  <div class="connection-level-radio-wrapper">
                    <rbz-form-radio
                      v-if="
                        newRowProps.data.securityPolicyId !== 'rbz-add-all-security-policies-option'
                      "
                      name="entry-connection-level"
                      id="entry-connection-level-path"
                      data-qa="entry-connection-level-path-radio"
                      v-model:model="newRowProps.data.connectionLevel"
                      :label="
                        $t(
                          'edgeFunction.configPage.securityPoliciesConnections.connectionLevel.path'
                        )
                      "
                      @input="newConnectionOnChangeConnectionLevel(newRowProps.data, 'path')"
                      :checked="newRowProps.data.connectionLevel === 'path'"
                    />
                    <rbz-form-radio
                      name="entry-connection-level"
                      id="entry-connection-level-site"
                      data-qa="entry-connection-level-site-radio"
                      v-model:model="newRowProps.data.connectionLevel"
                      :label="
                        $t(
                          'edgeFunction.configPage.securityPoliciesConnections.connectionLevel.site'
                        )
                      "
                      @input="newConnectionOnChangeConnectionLevel(newRowProps.data, 'site')"
                      :checked="newRowProps.data.connectionLevel === 'site'"
                      :disabled="
                        newConnectionCheckConnectedAnyEntry(newRowProps.data.securityPolicyId)
                      "
                    />
                    <rbz-form-radio
                      name="entry-connection-level"
                      id="entry-connection-level-all"
                      data-qa="entry-connection-level-all-radio"
                      :label="
                        $t(
                          'edgeFunction.configPage.securityPoliciesConnections.connectionLevel.all'
                        )
                      "
                      v-b-tooltip="
                        $t(
                          'edgeFunction.configPage.securityPoliciesConnections.connectionLevel.allTooltip'
                        )
                      "
                      @input="newConnectionOnChangeConnectionLevel(newRowProps.data, 'all')"
                      :checked="newRowProps.data.connectionLevel === 'all'"
                    />
                  </div>
                </td>
                <td class="new-data-cell">
                  <rbz-dropdown
                    type="multi-select"
                    class="new-security-policy-entry-selection match-selection"
                    :items="
                      newConnectionListSecurityPolicyEntries(newRowProps.data.securityPolicyId).map(
                        (item) => {
                          return [item.id, item.match];
                        }
                      )
                    "
                    :skip-sort="true"
                    v-if="newRowProps.data.connectionLevel === 'path'"
                    data-qa="new-security-policy-entry-dropdown"
                    v-model:value="newRowProps.data.entryIds"
                  />
                </td>
              </template>
            </template>
            <template
              #newRowOverrideConfirmationButtons
              v-if="newConnectionSecurityPolicies?.length === 1"
            >
              <template></template>
            </template>
          </rbz-table>
        </template>
      </rbz-card-collapsible>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="
          loadEdgeFunction(selectedEdgeFunction);
          loadDropdownData();
        "
        :disabled="readOnly"
      />
      <rbz-modal-dialog
        :show="showDeleteEdgeFunctionModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteEdgeFunction"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteEdgeFunctionModal = false"
        @closeModal="showDeleteEdgeFunctionModal = false"
        size="medium"
      >
        <template #content>
          {{
            $t('common.operations.delete.modal.message', { name: selectedEdgeFunctionData.name })
          }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import {
  BasicDocument,
  DocumentName,
  EdgeFunction,
  EdgeFunctionsPhaseType,
  SecurityPolicy,
  SecurityPolicyEntryMatch,
} from '@/types';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import RbzCardCollapsible from 'ui-component-library/rbz/components/RbzCardCollapsible.vue';
import PageTemplate from '@/views/PageTemplate.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import GitHistory from '@/components/GitHistory.vue';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import RbzCustomTag from '@/components/RbzCustomTag.vue';
import { useTagsStore } from '@/store/TagsStore';
import RbzFormRadio from '@/components/RbzFormRadio.vue';
import FormConfirmation from 'ui-component-library/rbz/components/FormConfirmation.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';

type Connection = {
  name: string;
  id: string;
  securityPolicyId: string;
  entryId: string;
  entryName: string;
  entryMatch: string;
};

type NewConnection = {
  securityPolicyId: SecurityPolicy['id'];
  connectionLevel: ConnectionLevel;
  entryIds: SecurityPolicyEntryMatch['id'][];
};

type ConnectionLevel = 'path' | 'site' | 'all';

export default defineComponent({
  name: 'EdgeFunctionsEditor',
  setup() {
    const { getEditPath, getListPath, navigateTo, normalizeRouteName } = useRouting();
    return {
      getEditPath,
      getListPath,
      navigateTo,
      normalizeRouteName,
    };
  },
  components: {
    RbzDropdown,
    RbzTable,
    RbzCustomTag,
    GitHistory,
    RbzFormButton,
    RbzFormRadio,
    FormConfirmation,
    RbzFormInput,
    RbzFormTextarea,
    LayoutDividerLine,
    PageTemplate,
    RbzModalDialog,
    RbzCardCollapsible,
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dropdownData: [] as BasicDocument[],
      isNew: false,
      selectedEdgeFunction: null as string,
      selectedEdgeFunctionData: null as EdgeFunction,
      originalEdgeFunctionData: null as EdgeFunction,
      pageLoadingCounter: null,
      isDeleteLoading: false,
      showDeleteEdgeFunctionModal: false,

      cloudPhases: [
        'request_pre',
        'request_post',
        'response_pre',
        'response_post',
      ] as EdgeFunctionsPhaseType[],
      referencedIds: [],
      securityPolicies: null as SecurityPolicy[],
      connectionsOriginal: null as Connection[],
      connections: null as Connection[],
      connectionsColumns: [
        {
          id: 'name',
          title: this.$t('edgeFunction.configPage.securityPoliciesConnections.name'),
          fieldNames: ['name'],
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'entry-name',
          title: this.$t('edgeFunction.configPage.securityPoliciesConnections.entryName'),
          fieldNames: ['entryName'],
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'entry-match',
          title: this.$t('edgeFunction.configPage.securityPoliciesConnections.entryMatch'),
          fieldNames: ['entryMatch'],
          displayFunction: (item: Connection) => {
            return item.entryMatch !== '__site_level__' ? item.entryMatch : '';
          },
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      connectionsRowButtons: [
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
        },
      ] as RbzTableRowButton[],
    };
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useTagsStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('edgeFunction.plural'),
        singular: this.$t('edgeFunction.singular'),
      };
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/edge-functions/versions`;
    },

    isLoading(): boolean {
      return (
        this.pageLoadingCounter === null || this.pageLoadingCounter > 0 || this.isDeleteLoading
      );
    },

    selectedEdgeFunctionProtected(): boolean {
      return this.selectedEdgeFunction?.startsWith('__');
    },

    selectedEdgeFunctionReferenced(): boolean {
      return this.referencedIds.includes(this.selectedEdgeFunction);
    },

    deleteTooltip(): string {
      if (this.selectedEdgeFunctionProtected) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      if (this.selectedEdgeFunctionReferenced) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return '';
    },

    entityNotSaved(): boolean {
      return !_.isEqual(this.selectedEdgeFunctionData, this.originalEdgeFunctionData);
    },

    automaticTags(): string[] {
      const allTag = `edge-function`;
      const nameTag = `edge-function-name:${this.tagsStore.textToTag(this.selectedEdgeFunctionData.name)}`;
      return [allTag, nameTag];
    },

    isEdgeFunctionValid(): boolean {
      const trimmedName = this.selectedEdgeFunctionData.name.toLowerCase().trim();
      const nameExists = this.dropdownData.filter((item) => {
        return item.name.toLowerCase().trim() === trimmedName;
      }).length;
      const nameValid =
        this.selectedEdgeFunctionData.name &&
        (trimmedName === this.originalEdgeFunctionData.name.toLowerCase().trim() || !nameExists);
      return nameValid;
    },

    getCloudPhasesDropdownItems(): [string, string][] {
      return this.cloudPhases.map((item) => {
        return [
          item,
          this.$t(`edgeFunction.phaseValue.${Utils.snakeCaseToCamelCase(item.toLowerCase())}`),
        ];
      });
    },

    newConnectionSecurityPolicies(): Partial<SecurityPolicy>[] {
      let securityPolicies = this.securityPolicies?.filter((securityPolicy) => {
        const notAllPathsConnected =
          this.newConnectionListSecurityPolicyEntries(securityPolicy.id).length > 0;
        const siteLevelConnected = this.newConnectionCheckConnectedSiteLevel(securityPolicy.id);
        return notAllPathsConnected && !siteLevelConnected;
      });
      securityPolicies = _.sortBy(securityPolicies, (item: SecurityPolicy) => {
        return item.name.toLowerCase();
      });
      securityPolicies.unshift({
        id: 'rbz-add-all-security-policies-option',
        name: this.$t('edgeFunction.configPage.securityPoliciesConnections.connectToAll'),
      });
      return securityPolicies;
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    redirectToList() {
      this.navigateTo(this.getListPath('edge-functions'));
    },

    switchEdgeFunction(edgeFunctionId: string) {
      this.navigateTo(this.getEditPath('edge-functions', edgeFunctionId));
    },

    async loadDropdownData() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/edge-functions`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.dropdownData = Utils.sortArrayByName(response?.data?.items) as BasicDocument[];
      this.setPageLoadingStatus(false);
    },

    newEdgeFunction(): EdgeFunction {
      const factory = DatasetsUtils.newEntityFactory['edge-functions'];
      return factory && factory();
    },

    async loadEdgeFunction(edgeFunctionId: string) {
      this.setPageLoadingStatus(true);
      this.selectedEdgeFunction = edgeFunctionId;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/edge-functions/${edgeFunctionId}`,
      });
      this.selectedEdgeFunctionData = response?.data || {};
      this.originalEdgeFunctionData = _.cloneDeep(this.selectedEdgeFunctionData);
      this.setPageLoadingStatus(false);
    },

    async duplicateEdgeFunction() {
      this.setPageLoadingStatus(true);
      const edgeFunctionToAdd = _.cloneDeep(this.selectedEdgeFunctionData);
      edgeFunctionToAdd.id = DatasetsUtils.generateUUID2();
      edgeFunctionToAdd.name = Utils.generateUniqueEntityName(
        edgeFunctionToAdd.name,
        _.map(this.dropdownData, 'name'),
        true
      );
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      const url = `conf/${this.selectedBranch}/edge-functions/${edgeFunctionToAdd.id}`;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: edgeFunctionToAdd,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.switchEdgeFunction(edgeFunctionToAdd.id);
      }
      this.setPageLoadingStatus(false);
    },

    async loadSecurityPolicies() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/security-policies`,
      });
      if (response) {
        this.securityPolicies = Utils.sortArrayByName(response?.data?.items) as SecurityPolicy[];
        this.loadConnections();
      }
      this.setPageLoadingStatus(false);
    },

    loadConnections() {
      this.connections = _.sortBy(
        _.flatMap(
          _.filter(this.securityPolicies, (securityPolicy) => {
            return _.some(securityPolicy.map, (mapEntry: SecurityPolicyEntryMatch) => {
              return mapEntry['edge_functions'].includes(this.selectedEdgeFunctionData.id);
            });
          }),
          (securityPolicy) => {
            return _.compact(
              _.map(securityPolicy.map, (mapEntry) => {
                if (mapEntry['edge_functions'].includes(this.selectedEdgeFunctionData.id)) {
                  return {
                    name: securityPolicy.name,
                    id: `${securityPolicy.id}-${mapEntry.id}`,
                    securityPolicyId: securityPolicy.id,
                    entryId: mapEntry.id,
                    entryName: mapEntry.name,
                    entryMatch: mapEntry.match,
                  } as Connection;
                } else {
                  return null;
                }
              })
            );
          }
        )
      );
      this.connectionsOriginal = _.cloneDeep(this.connections);
    },

    downloadEdgeFunction() {
      Utils.downloadFile('edge-function', 'json', this.selectedEdgeFunctionData);
    },

    openDeleteEdgeFunctionModal() {
      this.showDeleteEdgeFunctionModal = true;
    },

    async deleteEdgeFunction() {
      this.isDeleteLoading = true;
      const edgeFunctionId = this.selectedEdgeFunction;
      const dropdownIndex = _.findIndex(this.dropdownData, (item) => {
        return item.id === edgeFunctionId;
      });
      this.dropdownData.splice(dropdownIndex, 1);
      this.selectedEdgeFunction = null;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/edge-functions/${edgeFunctionId}`,
        successMessage,
        failureMessage,
      });
      this.redirectToList();
      this.isDeleteLoading = false;
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      let successMessage;
      let failureMessage;
      if (this.isNew) {
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }
      this.selectedEdgeFunctionData.name = this.selectedEdgeFunctionData.name.trim();
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: this.isNew ? 'POST' : 'PUT',
        url: `conf/${this.selectedBranch}/edge-functions/${this.selectedEdgeFunctionData.id}`,
        data: this.selectedEdgeFunctionData,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
      });
      // Update connected Security Policies
      const connectionsToAdd = _.filter(this.connections, (connection) => {
        return !_.some(this.connectionsOriginal, (connectionOriginal) => {
          return (
            connection.securityPolicyId === connectionOriginal.securityPolicyId &&
            connection.entryId === connectionOriginal.entryId
          );
        });
      });
      const connectionsToRemove = _.filter(this.connectionsOriginal, (connection) => {
        return !_.some(this.connections, (connectionOriginal) => {
          return (
            connection.securityPolicyId === connectionOriginal.securityPolicyId &&
            connection.entryId === connectionOriginal.entryId
          );
        });
      });
      const connectionsToUpdate = _.uniqBy(
        _.concat(connectionsToAdd, connectionsToRemove),
        'securityPolicyId'
      );
      await Promise.all(
        connectionsToUpdate.map(async (connection) => {
          const securityPolicy = this.getSecurityPolicy(connection.securityPolicyId);
          await RequestsUtils.sendRequest({
            host: this.userPlanet,
            methodName: 'PUT',
            url: `conf/${this.selectedBranch}/security-policies/${securityPolicy.id}`,
            data: securityPolicy,
          });
        })
      );
      if (response) {
        if (this.isNew) {
          const currentRoute = this.$route.path;
          const targetRoute = currentRoute.replace(
            'new',
            `config/${this.selectedEdgeFunctionData.id}`
          );
          this.$router.push(targetRoute);
        } else {
          await this.loadDropdownData();
          this.originalEdgeFunctionData = _.cloneDeep(this.selectedEdgeFunctionData);
        }
      }
      this.setPageLoadingStatus(false);
    },

    async loadReferencedIds() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/security-policies`,
      });
      const securityPolicies = response?.data?.items || [];
      const referencedIds: string[] = [];

      _.forEach(securityPolicies, (securityPolicy) => {
        _.forEach(securityPolicy.map, (location) => {
          referencedIds.push(location['edge_functions']);
        });
      });
      this.referencedIds = _.uniq(_.flatten(referencedIds));
      this.setPageLoadingStatus(false);
    },

    getConnectionDefaultValues(): NewConnection {
      const [defaultSecurityPolicy] = this.newConnectionSecurityPolicies || [];
      const securityPolicyId = defaultSecurityPolicy?.id;
      const connectionLevel: ConnectionLevel = 'site';
      const entryIds = ['__site_level__'];
      return {
        securityPolicyId,
        connectionLevel,
        entryIds,
      };
    },

    addConnection(newConnection: NewConnection) {
      if (!newConnection.securityPolicyId) {
        return;
      }
      let securityPolicies: SecurityPolicy[] = [];
      if (newConnection.securityPolicyId === 'rbz-add-all-security-policies-option') {
        securityPolicies = this.newConnectionSecurityPolicies.filter(
          (securityPolicy: Partial<SecurityPolicy>) => {
            return securityPolicy.id !== 'rbz-add-all-security-policies-option';
          }
        ) as SecurityPolicy[];
      } else {
        securityPolicies = [this.getSecurityPolicy(newConnection.securityPolicyId)];
      }
      securityPolicies.forEach((securityPolicy: SecurityPolicy) => {
        let entries: SecurityPolicyEntryMatch['id'][] = [];
        const connectedSiteLevel = this.newConnectionCheckConnectedSiteLevel(securityPolicy.id);
        const connectedAnyEntry = this.newConnectionCheckConnectedAnyEntry(securityPolicy.id);
        if (newConnection.connectionLevel === 'path' && !connectedSiteLevel) {
          entries = newConnection.entryIds;
        }
        if (newConnection.connectionLevel === 'site' && !connectedAnyEntry) {
          entries = ['__site_level__'];
        }
        if (newConnection.connectionLevel === 'all' && !connectedSiteLevel) {
          entries = _.map(securityPolicy.map, 'id').filter((id: SecurityPolicyEntryMatch['id']) => {
            return id !== '__site_level__';
          });
        }
        entries.forEach((id: string) => {
          const included = _.some(this.connections, (connection) => {
            return connection.securityPolicyId === securityPolicy.id && connection.entryId === id;
          });
          if (!included) {
            const mapEntry = securityPolicy?.map.find((entry) => {
              return entry.id === id;
            });
            this.connections.push({
              name: securityPolicy.name,
              id: `${securityPolicy.id}-${mapEntry.id}`,
              securityPolicyId: securityPolicy.id,
              entryId: mapEntry.id,
              entryName: mapEntry.name,
              entryMatch: mapEntry.match,
            });
            mapEntry['edge_functions'].push(this.selectedEdgeFunctionData.id);
          }
        });
      });
    },

    removeConnection(connectionToRemove: Connection) {
      this.connections = _.filter(this.connections, (connection) => {
        return (
          connection.securityPolicyId !== connectionToRemove.securityPolicyId ||
          connection.entryId !== connectionToRemove.entryId
        );
      });
      const securityPolicy = this.getSecurityPolicy(connectionToRemove.securityPolicyId);
      const mapEntry = securityPolicy?.map.find((entry) => {
        return entry.id === connectionToRemove.entryId;
      });
      mapEntry['edge_functions'] = _.filter(mapEntry['edge_functions'], (limitId) => {
        return limitId !== this.selectedEdgeFunctionData.id;
      });
    },

    setSessionDataWatcher() {
      this.$watch(
        () => [this.selectedEdgeFunctionData, this.entityNotSaved],
        (params) => {
          const [formData, entityNotSaved] = params;
          Utils.saveSessionData(formData, entityNotSaved);
        },
        {
          deep: true,
          immediate: true,
        }
      );
    },

    cancelAddConnection() {
      this.$refs.connectionsTable.setShowNewRow(false);
    },

    getSecurityPolicy(securityPolicyId: SecurityPolicy['id']): SecurityPolicy {
      return this.securityPolicies?.find((securityPolicy) => {
        return securityPolicy.id === securityPolicyId;
      });
    },

    newConnectionListSecurityPolicyEntries(
      securityPolicyId: SecurityPolicy['id']
    ): SecurityPolicyEntryMatch[] {
      const securityPolicy = this.getSecurityPolicy(securityPolicyId);
      return _.filter(securityPolicy?.map, (securityPolicyEntry: SecurityPolicyEntryMatch) => {
        return (
          securityPolicyEntry.id !== '__site_level__' &&
          !securityPolicyEntry['edge_functions'].includes(this.selectedEdgeFunctionData.id)
        );
      });
    },

    newConnectionCheckConnectedSiteLevel(securityPolicyId: SecurityPolicy['id']) {
      const securityPolicy = this.getSecurityPolicy(securityPolicyId);
      const siteLevelEntry = _.find(securityPolicy.map, ['id', '__site_level__']);
      return _.includes(siteLevelEntry?.['edge_functions'], this.selectedEdgeFunctionData.id);
    },

    newConnectionCheckConnectedAnyEntry(securityPolicyId: SecurityPolicy['id']) {
      const securityPolicy = this.getSecurityPolicy(securityPolicyId);
      return _.some(securityPolicy?.map, (securityPolicyEntry: SecurityPolicyEntryMatch) => {
        return (
          securityPolicyEntry.id !== '__site_level__' &&
          _.includes(securityPolicyEntry?.['edge_functions'], this.selectedEdgeFunctionData.id)
        );
      });
    },

    newConnectionOnChangeSecurityPolicy(newConnection: NewConnection) {
      if (newConnection.securityPolicyId === 'rbz-add-all-security-policies-option') {
        this.newConnectionOnChangeConnectionLevel(newConnection, 'site');
        return;
      }
      const isConnectedSiteLevel = this.newConnectionCheckConnectedAnyEntry(
        newConnection.securityPolicyId
      );
      if (newConnection.connectionLevel === 'path') {
        this.newConnectionOnChangeConnectionLevel(newConnection, 'path');
      } else if (newConnection.connectionLevel === 'site' && isConnectedSiteLevel) {
        this.newConnectionOnChangeConnectionLevel(newConnection, 'path');
      }
    },

    newConnectionOnChangeConnectionLevel(
      newConnection: NewConnection,
      newLevel: NewConnection['connectionLevel']
    ) {
      newConnection.connectionLevel = newLevel;
      switch (newConnection.connectionLevel) {
        case 'path': {
          newConnection.entryIds = [
            this.newConnectionListSecurityPolicyEntries(newConnection.securityPolicyId)?.[0].id,
          ];
          break;
        }
        case 'site': {
          newConnection.entryIds = ['__site_level__'];
          break;
        }
        case 'all': {
          newConnection.entryIds = [];
          break;
        }
      }
    },
  },
  created() {
    const tagsStore = useTagsStore();
    this.$watch(
      () => [tagsStore.editorDocId, tagsStore.editorDocType],
      async ([docId, docType]) => {
        if (!docId || docType !== 'edge-functions') {
          return;
        }
        await this.branchesStore.getBranches();
        await this.loadEdgeFunction(docId);
      },
      { immediate: true }
    );
    this.$watch(
      () => [this.$route.params?.['branch'], this.$route.params?.['doc_id']],
      async (routeParams) => {
        const [_newBranch, newId] = routeParams;
        const routeName: string = this.normalizeRouteName(this.$route.name as string);
        if (['New Edge Function', 'Edge Functions Editor'].includes(routeName)) {
          this.setPageLoadingStatus(true);
          this.isNew = routeName.startsWith('New');
          await this.branchesStore.getBranches();
          await this.loadDropdownData();
          if (this.isNew) {
            this.selectedEdgeFunctionData = this.newEdgeFunction();
            this.selectedEdgeFunctionData.name = Utils.generateUniqueEntityName(
              this.selectedEdgeFunctionData.name,
              _.map(this.dropdownData, 'name')
            );
            this.originalEdgeFunctionData = _.cloneDeep(this.selectedEdgeFunctionData);
            await this.loadSecurityPolicies();
          } else {
            const edgeFunctionId = newId as string;
            const dataExists = this.dropdownData.some((item) => {
              return item.id === edgeFunctionId;
            });
            await this.loadReferencedIds();
            if (dataExists) {
              await this.loadEdgeFunction(edgeFunctionId);
              await this.loadSecurityPolicies();
            } else {
              this.redirectToList();
              return;
            }
          }
          const savedSessionData = sessionStorage.getItem('formData');
          if (savedSessionData) {
            this.selectedEdgeFunctionData = JSON.parse(savedSessionData);
          }
          this.setSessionDataWatcher();
          this.setPageLoadingStatus(false);
        }
      },
      {
        deep: true,
        immediate: true,
      }
    );
  },
  beforeRouteLeave() {
    Utils.clearSessionData();
  },
});
</script>

<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.all-connected-data-cell,
.new-data-cell {
  color: map.get(colors.$colors, 'shuttle-gray');
  vertical-align: middle;
}

.connection-level-radio-wrapper {
  display: flex;
  gap: 16px;

  :deep(.radio-label) {
    color: map.get(colors.$colors, 'bright-gray');
  }
}
</style>
